// Context and hooks imports
import { useEffect, useRef } from "react";
import { useSelectedPage, useUpdatePageComponent, useUpdatePageProperties } from "../../Hooks/Display/usePages";
import useComponents, { useSelectedComponent } from "../../Hooks/Display/useComponents";

// Events imports
import { SET_COMPONENTS, SET_SELECTED_COMPONENT, TOGGLE_ADD_COMPONENT_DIALOG, TOGGLE_CONTEXT_MENU, TOGGLE_EXPAND } from "../../Context/Display/Components/ComponentsContextEvents";

// Components Imports
import ComponentsTreeView from "./Components/TreeView";
import NewComponentDialog from "./Components/NewComponentDialog";
import ContextMenu from "./Components/ContextMenu";
import DeleteComponentDialog from "./Components/DeleteComponentDialog";
import { TreeViewDragAnalyzer, TreeViewDragClue, moveTreeViewItem } from "@progress/kendo-react-treeview";
import { updatePage } from "../../API/Display/Display";
import { useChangeBooleans, useSaveComponent } from "../../Hooks/Display/useComponentProperties";
import useConfirmationDialog from "../Shared/ConfirmationWithContext/useConfirmationDialog";
import ResetComponentDialog from "./Components/ResetComponentDialog";

function Components() {

    const selectedPage = useSelectedPage();
    const selectedComponent = useSelectedComponent();

    const [state, dispatch] = useComponents();
    const dragClue = useRef(null);
    const [{
        isGeneralPropertiesChanged,
        isAttributeFormBuilderChanged,
        isActionTableChanged,
        isAttributeTableChanged,
        isReferenceTableChanged
    }] = useChangeBooleans();
    const [showConfirmationDialog, closeConfirmationDialog] = useConfirmationDialog();

    const saveComponent = useSaveComponent();
    const updatePageComponent = useUpdatePageComponent(selectedPage?.parentId, selectedPage?._id);
    const updatePageProperties = useUpdatePageProperties()

    useEffect(() => {
        if (selectedPage && selectedPage.components) {
            dispatch({ type: SET_COMPONENTS, payload: selectedPage.components });
            // different page is selected, so we need to reset the selected component
            // this was done because when we use save and continue, it would detect page change
            // and reset selected component index to 0
            if (selectedPage && selectedComponent && selectedPage._id !== selectedComponent.pageId) {
                dispatch({ type: SET_SELECTED_COMPONENT, payload: "0" });
            }
        } else if (!selectedPage || !selectedPage.components) {
            dispatch({ type: SET_COMPONENTS, payload: [] })
            dispatch({ type: SET_SELECTED_COMPONENT, payload: "0" });
        }
    }, [selectedPage])

    function handleNewComponentClick(event, selectedItem) {
        dispatch({
            type: TOGGLE_ADD_COMPONENT_DIALOG,
            payload: {
                parentId: selectedItem ? selectedItem.id : (selectedPage._id || selectedPage.id)
            }
        });
    }

    function handleExpandChange(event) {
        dispatch({
            type: TOGGLE_EXPAND,
            payload: event.item.treeIndex
        })
    }

    function toggleContextMenu(event, item) {
        event.preventDefault();
        if (item) {
            dispatch({
                type: TOGGLE_CONTEXT_MENU,
                payload: {
                    offset: { left: event.clientX, top: event.clientY },
                    index: item.treeIndex
                }
            })
        }
    }

    function handleDragOver(event) {
        const eventAnalyzer = new TreeViewDragAnalyzer(event).init();
        // const icon = getClueClassName(eventAnalyzer, state.components);
        dragClue.children.show(
            event.pageY - 180,
            event.pageX - 240,
            event.item.text
        )
    }

    function handleDragEnd(event) {
        dragClue.children.hide();
        const eventAnalyzer = new TreeViewDragAnalyzer(event).init();
        if (eventAnalyzer.isDropAllowed) {
            const reorderedTree = moveTreeViewItem(
                event.itemHierarchicalIndex,
                state.components,
                eventAnalyzer.getDropOperation(),
                eventAnalyzer.destinationMeta.itemHierarchicalIndex,
                null,
                "children"
            );
            updatePage(selectedPage._id, { components: reorderedTree });
            updatePageProperties(selectedPage.parentId, selectedPage._id, { components: reorderedTree })
        }
    }

    async function handleSelect(event) {
        let shouldContinue = true;
        if (isGeneralPropertiesChanged || isAttributeFormBuilderChanged || isActionTableChanged || isAttributeTableChanged || isReferenceTableChanged) {
            shouldContinue = await showConfirmationDialog({
                title: "Unsaved Changes",
                content: "You can save the changes made to this component. Then once you update the page, the changes made to all components of this change will be updated.",
                confirmButtonText: "Save",
                cancelButtonText: "Don't Save",
                onConfirm: handleSaveAndContinue
            });
            closeConfirmationDialog();
        }
        if (shouldContinue) {
            dispatch({
                type: SET_SELECTED_COMPONENT,
                payload: event.item.treeIndex
            })
        }
    }

    async function handleSaveAndContinue() {
        const updatedComponent = await saveComponent();
        updatePageComponent(selectedComponent.id, updatedComponent);
    }

    return (
        <div>
            <div className="d-flex justify-content-between align-items-center m-0 p-2">
                <h5>Components</h5>
                <button className="btn btn-primary cursor-pointer" onClick={handleNewComponentClick}>
                    <i className="fa-solid fa-plus" /> New Component
                </button>
            </div>
            {
                state.showAddComponentDialog &&
                <NewComponentDialog />
            }
            {
                state.showDeleteComponentDialog &&
                <DeleteComponentDialog />
            }
            {
                state.showResetComponentDialog &&
                <ResetComponentDialog />
            }
            <ContextMenu />
            <ComponentsTreeView
                data={state.components}
                expandField="expanded"
                textField="componentName"
                childrenField="children"
                expandIcons={true}
                draggable={true}
                onItemClick={handleSelect}
                onContextMenu={toggleContextMenu}
                onExpandChange={handleExpandChange}
                showAddSubItem={handleNewComponentClick}
                onItemDragOver={handleDragOver}
                onItemDragEnd={handleDragEnd}
            />
            <TreeViewDragClue ref={clue => dragClue.children = clue} />
        </div>
    )
}

export default Components;